import { TextContentElement } from '@deltek/specification-client/core/Models/textContentElement'
import clsx from 'clsx'
import { useOptionGroupStyles } from 'components/WorkspaceEditor/styles/useOptionGroupStyles'
import { nullOrUndefinedResolver } from 'components/WorkspaceEditor/utils'
import { useProjectSettings } from 'components/WorkspaceEditor/Workspace2Store'
import React, { memo } from 'react'
import { SelectionOption } from './SelectionOption'

/** Props for the UnitsOfMeasure component. */
export interface UnitsOfMeasureProps {
    /** The content item to render */
    contentItem: Partial<TextContentElement>
    /** Whether the component is inactive */
    inactive?: boolean
    /** Whether the component is editable */
    editable?: boolean
}
/**
 * A component that apply styling to render UnitsOfMeasure content
 * @param {Partial<TextContentElement>} contentItem - the object that holds the text property that needs to render imperial and metric units.
 */
export const UnitsOfMeasure = memo<UnitsOfMeasureProps>(
    ({ contentItem, inactive = false, editable = false }) => {
        const { id, imperialUnits, metricUnits } = nullOrUndefinedResolver(
            contentItem
        )
        const {
            editableText,
            editableTextBoxed,
            disabledBoxedInputContainer,
            notDisabledBoxedInputContainer,
            uomContainerInlineFlex,
            uomContainerContent,
        } = useOptionGroupStyles()
        const isBreakWord = imperialUnits.length >= 50
        const optionClasses = clsx(
            editableText,
            editableTextBoxed,
            inactive
                ? disabledBoxedInputContainer
                : notDisabledBoxedInputContainer
        )
        const containerClass = isBreakWord
            ? uomContainerContent
            : uomContainerInlineFlex
        const projectSettings = useProjectSettings()
        let unitOfMeasure =
            projectSettings?.unitOfMeasure.toString() ?? 'IMPERIAL'
        const upperCaseUOM = unitOfMeasure.toUpperCase()
        const isBothUnitsActive = upperCaseUOM === 'BOTH'
        const isImperialUnitsActive = upperCaseUOM === 'IMPERIAL'
        const isMetricUnitsActive = upperCaseUOM === 'METRIC'

        const renderOption = (label: string, type: 'imperial' | 'metric') => (
            <div
                key={`units-of-measure-${type}-${id || 'default'}`}
                data-testid={`units-of-measure-${type}-${id || 'default'}`}
                data-suggestion={`units-of-measure-${id || 'default'}`}
                className={containerClass}
            >
                <SelectionOption
                    contentItem={contentItem}
                    label={label}
                    classes={optionClasses}
                />
            </div>
        )

        return (
            <>
                {(isImperialUnitsActive || isBothUnitsActive) &&
                    renderOption(imperialUnits, 'imperial')}
                {(isMetricUnitsActive || isBothUnitsActive) &&
                    renderOption(metricUnits, 'metric')}
            </>
        )
    }
)
